// The one-time token in the URL, and everything that has to be true around it.
//
// WHY THIS FILE EXISTS AT ALL. xp/console/check.mjs asserts that its session
// token reaches sessionStorage and no cookie and no URL. This surface puts a
// credential in a URL. That is the adjacent thing on a sibling surface, so the
// difference has to be argued in the code rather than glossed — and the
// mitigations that make it defensible have to be somewhere a check can reach
// them, rather than being three lines inside a component.
//
// WHY A ONE-TIME LINK MAY SIT IN A URL WHERE A SESSION MAY NOT.
//
//   WHAT THE CONSOLE'S TOKEN IS. A standing capability. It opens every report an
//   operator can read, from anywhere, repeatedly, for as long as it lives.
//   Anywhere it comes to rest — a history entry, a proxy log, a screenshot, a
//   `Referer` — is a place somebody picks that capability up later and uses it
//   at their leisure. There is also nothing forcing it into a URL: the console
//   is already open when it gets one, so sessionStorage is available and free.
//
//   WHAT THIS IS. A one-time proof of one fact: control of one mailbox, at one
//   activation, for ten minutes. What it buys is a ticket to fill in a form
//   about yourself. It is spent by the first use, so the window in which a copy
//   is worth anything is the window before the person who was sent it clicks it.
//
//   AND THE PART THAT IS A REQUIREMENT RATHER THAN AN ARGUMENT. The link has to
//   work in a browser that has never seen this service. Somebody opens mail in
//   the Gmail app and lands in a fresh in-app webview with no storage, no
//   session and no relationship to the browser that asked for the code. A design
//   that required the requesting session would die in exactly the case the link
//   exists to serve. There is nowhere else for the secret to be.
//
// THAT ARGUMENT DOES NOT MAKE THE URL SAFE ON ITS OWN. Three things have to hold
// and each is asserted separately in check.mjs:
//
//   1. The token leaves the address bar before anything can read the URL —
//      including the first-party pulse post below. Not "before analytics if
//      somebody remembers": before anything, so the property is about the page.
//   2. Whatever is recorded is a virtual step path, never the live URL, and
//      never a path that still contains /v/.
//   3. The token never travels in a `Referer`. index.html's `no-referrer` is
//      that one, and the reasoning is there.
//
// ON ANALYTICS. There is still no third-party script on this surface, and
// check.mjs still asserts none reaches it. Measurement is a POST to this
// origin's /api/pulse. Hosting rewrites that path to a Cloud Function that
// forwards to Plausible. The browser never contacts plausible.io. The function
// strips tokens again. Console and kiosk are not measured. The holding page
// at / posts through its own first-party /pulse.js. A Global Privacy Control
// signal means nothing is posted.

import {
  JOIN_PATH_PREFIX,
  ONSITE_PATH_PREFIX,
  ONSITE_WHEEL_PATH_PREFIX,
  WHEEL_PATH_PREFIX,
  readJoinLink,
  readOnsiteLink,
  readOnsiteWheelLink,
  readWheelLink,
} from "./link.js";
import { globalPrivacyControlIsSet } from "./privacy/storage.js";

/** Where a verification token sits in the path, after the activation. */
export const TOKEN_SEGMENT = "/v/";

export type ArrivalLane = "public" | "onsite" | "none";

/**
 * Which registration product this path runs.
 *
 * `/j/` and `/w/` are both live on a stand at the same time, and they are
 * different products: the plain registration, and the same registration with a
 * spin at the end. The path is the only thing that says which one somebody
 * scanned, so it is read here and carried rather than asked of the stand — a
 * stand has both, so it cannot answer.
 *
 * It is path-driven for both public and onsite pages. Null only means the path
 * names no stand.
 */
export type PublicJoin = "registration" | "wheel" | null;

export type UrlReading =
  | { kind: "plain"; activationId: string | null; lane: ArrivalLane; join: PublicJoin }
  | {
      kind: "carries_a_token";
      activationId: string;
      token: string;
      strippedPath: string;
      lane: "public";
      join: PublicJoin;
    };

/**
 * Read a location's path, and say whether it is carrying a credential.
 *
 * Takes a string rather than reading `window.location`, so that it is testable
 * without a DOM and so that the two callers — the live page and the check — are
 * looking at the same function rather than at two readings of the same rule.
 *
 * THE QUERY STRING AND THE FRAGMENT ARE NOT CONSULTED, AND THAT IS DELIBERATE.
 * A token in a fragment is a popular trick, on the grounds that fragments are
 * not sent to servers. It is the wrong trick here: a fragment is invisible to
 * the server, so the page has to be delivered before the token can be read,
 * which means the token cannot be part of what the request is for. It also
 * survives in the address bar in exactly the same way. The path is honest about
 * what is happening.
 */
export function readUrl(pathname: string): UrlReading {
  const at = pathname.indexOf(TOKEN_SEGMENT);

  if (at === -1) {
    const join = readJoinLink(pathname);
    if (join.ok) {
      return { kind: "plain", activationId: join.activationId, lane: "public", join: "registration" };
    }
    const wheel = readWheelLink(pathname);
    if (wheel.ok) {
      return { kind: "plain", activationId: wheel.activationId, lane: "public", join: "wheel" };
    }
    const onsite = readOnsiteLink(pathname);
    if (onsite.ok) {
      return { kind: "plain", activationId: onsite.activationId, lane: "onsite", join: "registration" };
    }
    const onsiteWheel = readOnsiteWheelLink(pathname);
    if (onsiteWheel.ok) {
      return { kind: "plain", activationId: onsiteWheel.activationId, lane: "onsite", join: "wheel" };
    }
    return { kind: "plain", activationId: null, lane: "none", join: null };
  }

  const strippedPath = pathname.slice(0, at);
  const token = pathname.slice(at + TOKEN_SEGMENT.length).replace(/\/$/, "");
  const fromJoin = readJoinLink(strippedPath);
  const fromWheel = readWheelLink(strippedPath);
  const activation = fromJoin.ok ? fromJoin : fromWheel;

  // A malformed pair is not "a link with a bad activation", it is not a link.
  // Reported as plain so the page asks for an address rather than trying to
  // redeem something it cannot name a stand for — and the token is dropped here
  // rather than carried forward, because a token nothing can use is a credential
  // being kept for no reason. A token on /s/ is the same: onsite has no mailbox.
  if (!activation.ok || token.length === 0) {
    return { kind: "plain", activationId: null, lane: "none", join: null };
  }

  return {
    kind: "carries_a_token",
    activationId: activation.activationId,
    token,
    strippedPath,
    lane: "public",
    // The mail was sent from one of the two paths and comes back to the same
    // one, so the link itself is the record of which lane this person is on.
    join: fromJoin.ok ? "registration" : "wheel",
  };
}

/**
 * The path this page may be described by, anywhere, ever.
 *
 * One function rather than a convention, so that a live URL is never handed to
 * anything that records. A magic link becomes /j/<activation>, not
 * /j/<activation>/v/<token>. The pulse post does not use this path as the
 * Plausible page — that would make one URL per stand and hide the funnel.
 * `measuredPagePath` is the page that is recorded. This function is still the
 * last line of defence: if a live path is ever passed to `sanitizeMeasuredUrl`,
 * a token is already gone.
 */
export function measurablePath(pathname: string): string {
  const reading = readUrl(pathname);
  return reading.kind === "carries_a_token" ? reading.strippedPath : pathname;
}

/**
 * Everything the page needs in order to have removed the token from the URL.
 *
 * A description rather than a side effect, so that the check can assert what
 * would be done without a DOM, and so that the one place that touches history is
 * a single line at the top of the entry point.
 *
 * `replaceState` rather than `pushState`, because pushing would leave the
 * token-bearing URL one back-button press away — which is worse than leaving it
 * in the bar, since it looks as though it has gone.
 */
export type Strip = { replaceWith: string; alreadyClean: boolean };

export function stripToken(pathname: string): Strip {
  const reading = readUrl(pathname);
  return reading.kind === "carries_a_token"
    ? { replaceWith: reading.strippedPath, alreadyClean: false }
    : { replaceWith: pathname, alreadyClean: true };
}

/**
 * Do it, first thing, and hand back what was found.
 *
 * THE ORDER IN HERE IS THE WHOLE POINT AND IT IS EASY TO GET BACKWARDS. The
 * token is read out of the location and the address bar is rewritten before this
 * function returns, so there is no window in which the page has started doing
 * anything — rendering, fetching, measuring — with a credential still in the
 * bar. A version that rewrote the bar after the redemption request would be
 * correct almost always and wrong exactly when the network is slow, which at a
 * trade show is most of the time.
 *
 * The token is returned rather than stashed. Nothing writes it to storage:
 * privacy/storage.ts's permitted list is empty and this is not an entry. It
 * lives in a closure for the length of one redemption and then it is gone,
 * which is the same lifetime the row in the database gives it.
 */
export function takeTokenFromTheAddressBar(location: Location, history: History): UrlReading {
  const reading = readUrl(location.pathname);

  if (reading.kind === "carries_a_token") {
    history.replaceState(null, "", `${reading.strippedPath}${location.search}${location.hash}`);
  }

  return reading;
}

/**
 * The prefix a join link starts with, re-exported so a caller needs one import.
 *
 * Not a second definition. link.ts owns it, and a check compares them, because
 * two constants spelling the same path is how a QR code and the page it points
 * at come to disagree.
 */
export { JOIN_PATH_PREFIX, ONSITE_PATH_PREFIX, ONSITE_WHEEL_PATH_PREFIX, WHEEL_PATH_PREFIX };

// ---------------------------------------------------------- first-party pulse
//
// Each stage of the join and onsite flows is a page in Plausible, so drop-off
// is visible as a funnel rather than as one hit for the whole SPA. The paths
// are virtual: /j/<activation>/address, /s/<activation>/details. They are not
// the address bar, and they never include a token.
//
// THE SHOW'S ID IS IN THEM SINCE 9 OCT 2026 (Gabe: "we will want per show
// metrics"). Without it every show's visits were one number on the site, and
// the dashboard could only say "Not connected" for every show but Ride. An
// activation id is not a person: it names a show, which every visitor to the
// page already knows. The funnel across all shows is a wildcard goal now
// (/j/*/address) rather than a plain path.
//
// These two lists are restated in xp/pulse/rules.js. check.mjs compares them.

export const MEASURED_STEPS = [
  "start",
  "address",
  "code",
  "details",
  "terms",
  "survey",
  "done",
  "refused",
  "unusable",
] as const;

export type MeasuredStep = (typeof MEASURED_STEPS)[number];
export type MeasuredLane = "public" | "onsite" | "onsite-wheel";

export const MEASURED_HOSTS: Readonly<Record<string, string>> = {
  "reg.novaexhibits.com": "reg.novaexhibits.com",
  "reg-staging.novaexhibits.com": "reg-staging.novaexhibits.com",
};

/** Same-origin beacon. A relative path, so this file names no other server. */
export const PULSE_PATH = "/api/pulse";

export function isMeasuredStep(value: string): value is MeasuredStep {
  return (MEASURED_STEPS as readonly string[]).includes(value);
}

/** A show's id as the pulse may carry it: a lowercase uuid, or nothing. */
const ACTIVATION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function measuredPagePath(lane: MeasuredLane, step: MeasuredStep, activationId: string): string {
  const prefix =
    lane === "onsite-wheel"
      ? ONSITE_WHEEL_PATH_PREFIX
      : lane === "onsite"
        ? ONSITE_PATH_PREFIX
        : JOIN_PATH_PREFIX;
  return `${prefix}${activationId.toLowerCase()}/${step}`;
}

export function siteForHost(hostname: string): string | null {
  return MEASURED_HOSTS[hostname.toLowerCase()] ?? null;
}

/**
 * Rebuild a candidate URL as the only string Plausible may be given.
 *
 * Same rules as xp/pulse/rules.js `sanitizeMeasuredUrl`. Takes the Hosting
 * site as an argument so a payload cannot pick the other property.
 */
export function sanitizeMeasuredUrl(candidate: string, site: string): string | null {
  if (site.length === 0) return null;
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
  if (parsed.username || parsed.password) return null;
  if (parsed.search || parsed.hash) return null;
  if (parsed.pathname.includes(TOKEN_SEGMENT)) return null;

  const stripped = measurablePath(parsed.pathname);

  const document = stripped.replace(/\/+$/, "") || "/";
  if (document === "/" || document === "/mail") return ["https:", "", site, ""].join("/");

  const match = /^\/(j|s|sw)\/([0-9a-f-]{36})\/([a-z]+)$/.exec(stripped);
  const lane = match?.[1];
  const activation = match?.[2];
  const step = match?.[3];
  if (lane === undefined || activation === undefined || step === undefined) return null;
  if (!ACTIVATION_ID.test(activation) || !isMeasuredStep(step)) return null;

  return ["https:", "", site, `${lane}/${activation}/${step}`].join("/");
}

export type PulsePage = {
  readonly hostname: string;
  readonly origin: string;
  readonly width: number;
  readonly globalPrivacyControl?: unknown;
};

/**
 * The JSON body posted to /api/pulse, or null if this page must stay quiet.
 *
 * Null for: a host we do not measure (localhost, *.web.app, the marketing
 * site), a Global Privacy Control signal, a step that is not on the list, or a
 * URL that would still carry a token. The function rebuilds `u` and `d` anyway;
 * this is the half that keeps a bad call from leaving the browser.
 */
export function buildPulseBody(
  lane: MeasuredLane,
  step: MeasuredStep,
  page: PulsePage,
  activationId: string,
): string | null {
  if (globalPrivacyControlIsSet({ globalPrivacyControl: page.globalPrivacyControl })) return null;
  const site = siteForHost(page.hostname);
  if (site === null) return null;
  if (!isMeasuredStep(step)) return null;
  if (!ACTIVATION_ID.test(activationId.toLowerCase())) return null;

  const path = measuredPagePath(lane, step, activationId);
  if (path.includes(TOKEN_SEGMENT)) return null;

  let origin: URL;
  try {
    origin = new URL(page.origin);
  } catch {
    return null;
  }
  origin.pathname = path;
  origin.search = "";
  origin.hash = "";

  const url = sanitizeMeasuredUrl(origin.href, site);
  if (url === null || url.includes(TOKEN_SEGMENT)) return null;

  const width = Number(page.width);
  return JSON.stringify({
    n: "pageview",
    u: url,
    d: site,
    r: "",
    w: Number.isFinite(width) && width > 0 && width < 8000 ? Math.round(width) : 0,
  });
}

let lastPulse = "";

/**
 * Record this step, after the token has left the address bar.
 *
 * fetch rather than sendBeacon, and credentials omitted, for the same reason
 * the attend transport is written that way: a cookie added later must not
 * start travelling because a beacon defaults to including it. Failures are
 * swallowed — losing a count must not take the form down.
 */
export function recordMeasuredPage(lane: MeasuredLane, step: MeasuredStep, activationId: string): void {
  if (typeof window === "undefined") return;
  const body = buildPulseBody(
    lane,
    step,
    {
      hostname: window.location.hostname,
      origin: window.location.origin,
      width: window.innerWidth,
      globalPrivacyControl: (navigator as { globalPrivacyControl?: unknown }).globalPrivacyControl,
    },
    activationId,
  );
  if (body === null) return;
  if (lastPulse === body) return;
  lastPulse = body;

  void fetch(PULSE_PATH, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body,
    credentials: "omit",
    referrerPolicy: "no-referrer",
    keepalive: true,
  }).catch(() => undefined);
}

